"use client";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import ModalIconTitle from "../ModalIconTitle";
import { CgCalendarToday } from "react-icons/cg";
import { AxiosResponse } from "axios";

import TextAreaInput from "../../components/Modal//Input/TextAreaInput";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { UpdateRecordRequest } from "../../class/record.schema";
import toast from "react-hot-toast";
import { CronJobViewObject, UpdateCronRequest } from "../../class/cron.schema";
import { BackendApi } from "../../utils/BackendApi";
import { ApiRoute } from "../../utils/ApiRoute";
import { SystemMsg } from "../../utils/SystemMsg";
import { useI18n } from "../../i18n";

type CronModalProp = {
  toast: typeof toast;
  mode: string;
  show: boolean;
  onSaveBtnClick: (
    resp: AxiosResponse,
    mode?: string,
    data?: CronJobViewObject,
  ) => void;
  onClose: () => void;
  data: CronJobViewObject | undefined;
};

export default function CronModal({
  toast,
  mode,
  show,
  onSaveBtnClick,
  onClose,
  data,
}: CronModalProp) {
  const { t } = useI18n();
  const [modeState, setModeState] = useState<"add" | "edit">(`add`);

  const [id, setId] = useState(-1);
  const [name, setName] = useState<string | undefined>();
  const [jobNames, setJobNames] = useState<string[]>([]);
  const [description, setDescription] = useState<string | undefined>();

  const [modalTitle, setModalTitle] = useState("Add Cron Job");

  const dialogClassName = useModalWidthClass();

  useEffect(() => {
    if (!show) {
      return;
    }

    const isEditOrView = mode === "edit" || mode === "view";
    if (isEditOrView) {
      if (!data || Object.keys(data).length === 0) {
        toast.error(`Unable to get event data!`);
        console.error(`Unable to get event data!`);
        onHide();
        return;
      }
    }

    convertDataFromGrid(mode, data);

    const modeLower = mode.toString().toLowerCase();
    if (modeLower === `add`) {
      setModeState(`add`);
    }
    if (modeLower === `edit`) {
      setModeState(`edit`);
    }
  }, [show, mode, data]);

  useEffect(() => {
    if (modeState === `add`) {
      setModalTitle(t("cron.addCronJob"));
    }
    if (modeState === `edit`) {
      setModalTitle(t("cron.editCronJob"));
    }
  }, [modeState]);

  useEffect(() => {
    if (!show) {
      return;
    }
    BackendApi.get(
      `${ApiRoute.cron}/jobNames`,
      {},
      (resp: AxiosResponse) => {
        const names = Array.isArray(resp.data)
          ? resp.data.filter((item) => typeof item === "string")
          : [];
        setJobNames(names);
      },
      () => {
        setJobNames([]);
        toast.error(SystemMsg.Common.unableToLoadData);
      },
    );
  }, [show]);

  const convertDataFromGrid = (mode: string, data: any) => {
    const isEditOrView = mode === "edit";
    if (isEditOrView) {
      const record = data as CronJobViewObject;
      setId(record.id);
      setName(record.name);
      setDescription(record.description);
    }
  };

  const saveEventClick = async () => {
    if (!name || name?.toString().length === 0) {
      toast.error(SystemMsg.Common.pleaseEnterAValidX(`Name`));
      return;
    }
    if (!description || description?.toString().length === 0) {
      toast.error(SystemMsg.Common.pleaseEnterAValidX(`Description`));
      return;
    }
    if (modeState === `add`) {
      await addEventAction(modeState, getSendData(), {
        ...getSendData(),
        id: id,
      });
    }
    if (modeState === `edit`) {
      await editEventAction(id, modeState, getSendData(), {
        ...getSendData(),
        id: id,
      });
    }
  };

  const addEventAction = async (
    mode: string,
    sendData: UpdateCronRequest,
    callbackData: any,
  ) => {
    const succFunc = (resp: AxiosResponse) => {
      // eventObj.id = id;
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp, mode, callbackData);
      }
      onHide();
    };
    const errFunc = (resp: AxiosResponse | string) => {
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp as AxiosResponse, mode, callbackData);
      }
    };
    return BackendApi.post(ApiRoute.cron, sendData, succFunc, errFunc);
  };

  const editEventAction = async (
    id: number,
    mode: string,
    sendData: UpdateRecordRequest,
    callbackData: any,
  ) => {
    const succFunc = (resp: AxiosResponse) => {
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp, mode, callbackData);
      }
      onHide();
    };
    const errFunc = (resp: AxiosResponse | string) => {
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp as AxiosResponse, mode, callbackData);
      }
    };
    return BackendApi.put(
      `${ApiRoute.cron}/${id}`,
      sendData,
      succFunc,
      errFunc,
    );
  };

  const getSendData = (): UpdateCronRequest => {
    return {
      name: name,
      description: description,
    } as UpdateRecordRequest;
  };

  const onHide = () => {
    setId(-1);
    setName(undefined);
    setDescription(undefined);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const nameOptions =
    name && !jobNames.includes(name) ? [name, ...jobNames] : jobNames;

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      dialogClassName={dialogClassName}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <ModalIconTitle
            title={modalTitle}
            iconElement={<CgCalendarToday />}
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="cronModal.name">
            <Form.Label>{t("fields.name")}</Form.Label>
            <Form.Select
              value={name ?? ""}
              isInvalid={name !== undefined && name.toString().length === 0}
              onChange={(event) => {
                const value = event.target.value;
                setName(value.length > 0 ? value : undefined);
              }}
            >
              <option value="">{t("common.pleaseSelect")}</option>
              {nameOptions.map((jobName) => (
                <option key={jobName} value={jobName}>
                  {jobName}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              Please select the Name!
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        <TextAreaInput
          placeholder={t("fields.description")}
          value={description}
          setValueFunc={(value: string | number) => {
            setDescription(value as string);
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={saveEventClick}>
          {t("common.save")}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          {t("common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
